import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Clock, RefreshCw, XCircle } from "lucide-react";
import { z } from "zod/v4";

import { AsyncTasksTable } from "~/components/admin/AsyncTasksTable";
import { Breadcrumbs } from "~/components/admin/Breadcrumbs";
import { Select } from "~/components/admin/form";
import { AsyncTaskIcon } from "~/components/admin/Icons";
import { PageHeader } from "~/components/admin/PageHeader";
import { StatCard } from "~/components/admin/StatCard";
import { QueryState } from "~/components/QueryState";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { Button } from "~/components/ui/Button";
import { Spinner } from "~/components/ui/Spinner";
import { cn } from "~/lib/cn";
import { useTRPC } from "~/lib/trpc";

const MAX_AGE_OPTIONS = [
  { value: "PT15S", label: "Last 15 seconds" },
  { value: "PT30S", label: "Last 30 seconds" },
  { value: "PT1M", label: "Last 1 minute" },
  { value: "PT5M", label: "Last 5 minutes" },
  { value: "PT10M", label: "Last 10 minutes" },
  { value: "PT15M", label: "Last 15 minutes" },
  { value: "PT30M", label: "Last 30 minutes" },
  { value: "PT45M", label: "Last 45 minutes" },
  { value: "PT1H", label: "Last 1 hour" },
  { value: "PT3H", label: "Last 3 hours" },
  { value: "PT6H", label: "Last 6 hours" },
  { value: "PT12H", label: "Last 12 hours" },
  { value: "P1D", label: "Last 1 day" },
  { value: "P2D", label: "Last 2 days" },
  { value: "P7D", label: "Last 7 days" },
  { value: "P14D", label: "Last 14 days" },
  { value: "P30D", label: "Last 30 days" },
] as const;

const TasksSearchSchema = z.object({
  status: z.enum(["queued", "running", "succeeded", "failed"]).optional(),
  resource: z.enum(["requirements", "evaluation"]).optional(),
  maxAge: z.enum(MAX_AGE_OPTIONS.map((option) => option.value)).optional(),
});

const STATUS_CARDS = [
  { status: "queued", label: "Queued", icon: Clock },
  { status: "running", label: "Running", icon: RefreshCw },
  { status: "succeeded", label: "Succeeded", icon: CheckCircle2 },
  { status: "failed", label: "Failed", icon: XCircle },
] as const;

export const Route = createFileRoute("/_authenticated/admin/aviary/tasks/")({
  component: TasksIndex,
  validateSearch: (search) => TasksSearchSchema.parse(search),
});

function TasksIndex() {
  const trpc = useTRPC();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { status, resource } = search;
  const maxAge = search.maxAge ?? "P1D";

  const statsQuery = useQuery(
    trpc.aviary.tasks.stats.queryOptions(
      { maxAge },
      { refetchInterval: 30_000 },
    ),
  );

  const listQuery = useQuery(
    trpc.aviary.tasks.list.queryOptions(
      { status, resource, maxAge },
      { refetchInterval: 30_000 },
    ),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Link to="/admin">Admin</Link>
            <span>Aviary Tasks</span>
          </Breadcrumbs>
        }
        icon={AsyncTaskIcon}
        title="Aviary Tasks"
        description="Async tasks created by the Aviary API"
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void listQuery.refetch();
              void statsQuery.refetch();
            }}
          >
            <RefreshCw
              className={cn(
                "mr-2 h-4 w-4",
                (listQuery.isFetching || statsQuery.isFetching) &&
                  "animate-spin",
              )}
            />
            Refresh
          </Button>
        }
      />
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        {STATUS_CARDS.map((card) => (
          <Link
            key={card.status}
            to="/admin/aviary/tasks"
            search={{
              ...search,
              status: card.status === status ? undefined : card.status,
            }}
            className="contents"
          >
            <StatCard
              label={card.label}
              icon={<card.icon className="h-7 w-7" />}
              value={statsQuery.data?.[card.status] ?? 0}
              isLoading={statsQuery.isLoading}
              error={statsQuery.error}
              clickable
            />
          </Link>
        ))}
      </div>
      <QueryState
        result={listQuery}
        loading={
          <div className="flex justify-center items-center py-24">
            <Spinner size="lg" />
          </div>
        }
        error={(error) => <TRPCClientErrorDisplay error={error} />}
      >
        {(data) => (
          <AsyncTasksTable
            data={data}
            actions={
              <>
                <div className="w-44">
                  <Select
                    items={MAX_AGE_OPTIONS.map(({ value, label }) => ({
                      label,
                      value,
                    }))}
                    value={maxAge}
                    onValueChange={(value) => {
                      if (value != null) {
                        void navigate({
                          search: (prev) => ({ ...prev, maxAge: value }),
                          replace: true,
                        });
                      }
                    }}
                  />
                </div>
                <div className="w-40">
                  <Select
                    items={[
                      { label: "All statuses", value: null },
                      ...STATUS_CARDS.map((card) => ({
                        label: card.label,
                        value: card.status,
                      })),
                    ]}
                    value={status ?? null}
                    onValueChange={(value) => {
                      void navigate({
                        search: (prev) => ({
                          ...prev,
                          status: value ?? undefined,
                        }),
                        replace: true,
                      });
                    }}
                  />
                </div>
                <div className="w-44">
                  <Select
                    items={[
                      { label: "All resources", value: null },
                      { label: "Requirements", value: "requirements" as const },
                      { label: "Evaluation", value: "evaluation" as const },
                    ]}
                    value={resource ?? null}
                    onValueChange={(value) => {
                      void navigate({
                        search: (prev) => ({
                          ...prev,
                          resource: value ?? undefined,
                        }),
                        replace: true,
                      });
                    }}
                  />
                </div>
              </>
            }
          />
        )}
      </QueryState>
    </div>
  );
}
